import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

const PROVIDERS = [
  { code: "mock", name: "Mock (No real IRP call)", desc: "IRN generated locally via SHA-256 hash. Use for demo/testing." },
  { code: "cleartax", name: "ClearTax", desc: "Requires: client_id, client_secret, user_gstin. Sandbox URL: einv-apisandbox.nic.in via ClearTax proxy." },
  { code: "masters_india", name: "Masters India", desc: "Requires: auth_token, user_gstin. GST Suvidha Provider (GSP)." },
  { code: "iris", name: "IRIS", desc: "Requires: client_id, client_secret. Enterprise-grade GSP." },
];

export default function GspConfig() {
  const [cfg, setCfg] = useState({ provider: "mock", client_id: "", client_secret: "", auth_token: "", user_gstin: "", sandbox: true });
  const [current, setCurrent] = useState(null);

  const load = async () => {
    const { data } = await api.get("/settings/gsp");
    setCurrent(data);
    setCfg((c) => ({ ...c, provider: data.provider || "mock",
                     client_id: data.client_id || "", user_gstin: data.user_gstin || "",
                     sandbox: data.sandbox !== false }));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/settings/gsp", cfg);
      toast.success("GSP config saved");
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const p = PROVIDERS.find((x) => x.code === cfg.provider);

  return (
    <div className="max-w-3xl space-y-5 lb-fade" data-testid="gsp-config-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Compliance</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">e-Invoice GSP</h1>
        <p className="text-neutral-600 text-sm mt-1">
          Configure your GST Suvidha Provider (GSP) for real IRN generation via IRP.
          {current?.configured
            ? <span className="ml-2 lb-badge lb-badge-success"><CheckCircle size={12} weight="fill" /> Configured</span>
            : <span className="ml-2 lb-badge lb-badge-warning"><WarningCircle size={12} /> Not configured — using mock IRN</span>}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {PROVIDERS.map((prov) => (
          <button key={prov.code}
                  onClick={() => setCfg({ ...cfg, provider: prov.code })}
                  className={"text-left p-4 border-2 " + (cfg.provider === prov.code ? "border-[#0A66C2] bg-neutral-50" : "border-neutral-200 hover:border-neutral-400")}
                  data-testid={`gsp-${prov.code}`}>
            <div className="font-display font-bold">{prov.name}</div>
            <div className="text-xs text-neutral-500 mt-1">{prov.desc}</div>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="lb-card p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
          Credentials for {p?.name}
        </div>

        {cfg.provider !== "mock" && (
          <div className="grid grid-cols-2 gap-4">
            {(cfg.provider === "cleartax" || cfg.provider === "iris") && (
              <>
                <div>
                  <label className="lb-label">Client ID</label>
                  <input className="lb-input font-mono" value={cfg.client_id}
                         onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })} data-testid="gsp-client-id" />
                </div>
                <div>
                  <label className="lb-label">Client Secret</label>
                  <input type="password" className="lb-input font-mono" value={cfg.client_secret}
                         onChange={(e) => setCfg({ ...cfg, client_secret: e.target.value })} data-testid="gsp-secret" />
                </div>
              </>
            )}
            {cfg.provider === "masters_india" && (
              <div className="col-span-2">
                <label className="lb-label">Auth Token</label>
                <input type="password" className="lb-input font-mono" value={cfg.auth_token}
                       onChange={(e) => setCfg({ ...cfg, auth_token: e.target.value })} data-testid="gsp-token" />
              </div>
            )}
            <div>
              <label className="lb-label">User GSTIN</label>
              <input className="lb-input font-mono uppercase" maxLength={15} value={cfg.user_gstin}
                     onChange={(e) => setCfg({ ...cfg, user_gstin: e.target.value.toUpperCase() })} />
            </div>
            <label className="flex items-center gap-2 self-end text-sm">
              <input type="checkbox" checked={cfg.sandbox}
                     onChange={(e) => setCfg({ ...cfg, sandbox: e.target.checked })} />
              Use sandbox environment
            </label>
          </div>
        )}

        <div className="pt-3 border-t border-neutral-200 flex gap-2">
          <button className="lb-btn" data-testid="gsp-submit">Save Configuration</button>
        </div>
      </form>

      <div className="lb-card p-4 text-xs text-neutral-500 bg-neutral-50">
        <strong>Note:</strong> Real IRN generation requires live GSP sandbox credentials from your chosen provider. Until configured, the system generates mock IRNs (SHA-256 hashes) for demo purposes. QR codes are always real.
      </div>
    </div>
  );
}
